import * as React from 'react';
import { useNote } from '@/hooks/useNote';
import { EmptyState } from '@/components/custom/empty-state';
import { useTranslation } from 'react-i18next';
import { confirm } from '@/lib/confirm';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { useSession } from '@/hooks/useSession';
import { Tooltip } from 'react-tooltip';
import './css/notes-editor.css';
import { NotesViewHeader, NotesViewInnerHeader, NotesViewTitle, NotesViewDate, NotesImportantBtn, NotesViewBody } from './notes';

export function NotesEditor(props) {
  const { t } = useTranslation();
  const { note, emptyStateTitle, emptyStateText } = props;
  const [importantNote, setImportantNote] = React.useState(false);
  const { nextBody, setNextBody, setBookmarked, unmarkNote } = useNote();
  const { session } = useSession();

  const isOwnerNote = session?.user?.name === note?.user?.name;

  React.useEffect(() => {
    setNextBody(note?.body || '');
    setImportantNote(note?.bookmarked);
  }, [note, setNextBody]);

  const onChange = (event, editor) => {
    const data = editor.getData();
    setNextBody(data);
  };

  const handleUncheck = async () => {
    const confirmed = await confirm({
      title: 'Deseja mesmo desmarcar essa anotação?',
      description: 'Ela sairá da sua lista de anotações importantes',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      unmarkNote(note.id, note);
      setImportantNote(false);
    }
  };

  const handleChecked = () => {
    setBookmarked(note.id, note);
    setImportantNote(true);
  };

  return note?.id ? (
    <>
      <NotesViewHeader>
        <NotesViewInnerHeader>
          <NotesViewTitle>{note.title}</NotesViewTitle>
          {isOwnerNote ? (
            <NotesViewDate>
              Criação em {note?.['created_at']} • {t('projects.updatedIn')} {note?.['updated_at']}
            </NotesViewDate>
          ) : (
            <NotesViewDate>
              {t('projects.updatedIn')} {note?.['updated_at']}
            </NotesViewDate>
          )}
        </NotesViewInnerHeader>

        <Tooltip id="important" />
        <NotesImportantBtn
          added={importantNote}
          type="button"
          data-tooltip-id="important"
          data-tooltip-content="Salvar como importante"
          onClick={() => (!importantNote ? handleChecked() : handleUncheck())}
        >
          <svg
            width="17"
            height="21"
            viewBox="0 0 17 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.7913 15.2917L8.49967 20.0834L1.20801 15.2917V2.83341C1.20801 2.32508 1.4275 1.83757 1.8182 1.47813C2.2089 1.11868 2.73881 0.916748 3.29134 0.916748H13.708C14.2605 0.916748 14.7904 1.11868 15.1811 1.47813C15.5718 1.83757 15.7913 2.32508 15.7913 2.83341V15.2917Z"
              fill="white"
              stroke="#5D6A86"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </NotesImportantBtn>
      </NotesViewHeader>

      <NotesViewBody>
        {isOwnerNote ? (
          <CKEditor
            editor={Editor}
            note={note}
            onChange={onChange}
            data={nextBody || ''}
            config={{
              placeholder: 'Comece a sua nota por aqui!',
              toolbar: {
                shouldNotGroupWhenFull: true,
                shouldNotGroupWhenOne: true
              }
            }}
          />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: note?.body }}></div>
        )}

        {note.bookmarked && note.bookmarked_count === 2 && (
          <div
            className="alert u-mt-2"
            role="alert"
          >
            <span> Você e {note.bookmarked_count - 1} participante marcaram essa anotação como importante</span>
          </div>
        )}
        {note.bookmarked && note.bookmarked_count > 2 && (
          <div
            className="alert u-mt-2"
            role="alert"
          >
            <span> Você e {note.bookmarked_count - 1} participantes marcaram essa anotação como importante</span>
          </div>
        )}
      </NotesViewBody>
    </>
  ) : (
    <EmptyState
      type="select"
      title={emptyStateTitle || t('emptyState.titleNotes')}
      text={emptyStateText || t('emptyState.textNotes')}
      variant="plain"
    />
  );
}
